import { ColumnDef } from '@tanstack/react-table';
import { ActiveSourcingChannelDetails } from 'api-clients/monolith';
import { isPast } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';

import CheckIcon from '@/assets/icons/check.svg';
import EditIcon from '@/assets/icons/edit.svg';
import RemoveCloud from '@/assets/icons/remove-cloud.svg';
import { ReturnBackArrowIcon } from '@/components/icons/return-back-arrow';
import { ApplicantStats } from '@/components/ui/applicant-stats';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { ConversionStats } from '@/components/ui/conversion-stats';
import { CurrencyStats } from '@/components/ui/currency-stats';
import { Duration } from '@/components/ui/duration';
import { Tag } from '@/components/ui/tag';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useHiringGoalIdParam } from '@/hooks/use-hiring-goal-id-param';
import { BOOST, OTHER, POOL, REACH, ZIPRECRUITER } from '@/types/channels';
import { formatShortDate } from '@/utils/date-util';

import { ToggleCampaign } from '../toggle-campaign';
import { Timestamp } from './timestamp';
import { SetReachPublisherStatus } from './use-reach-channels';
interface ChannelColumnsProps {
  brand?: string;
  location?: string;
  funnel?: string;
  setReachPublisherStatus?: SetReachPublisherStatus;
}

export function useChannelColumns({
  brand,
  location,
  funnel,
  setReachPublisherStatus,
}: ChannelColumnsProps): ColumnDef<ActiveSourcingChannelDetails>[] {
  const intl = useIntl();
  const navigate = useNavigate();
  const { withHiringGoalId } = useHiringGoalIdParam();
  const [searchParams] = useSearchParams();

  const handleCampaignEdit = (rowItem: ActiveSourcingChannelDetails) => {
    const {
      sourcing_purchase_id: sourcingPurchaseId,
      sourcing_channel: { slug },
    } = rowItem;

    if (!sourcingPurchaseId || !slug) return undefined;

    let channelSlug = slug;
    // Reach campaigns will ideally have the publisher slug
    if (rowItem.reach_campaign_id) {
      channelSlug = REACH;
    }

    return () => {
      navigate({
        pathname: `${channelSlug}/${sourcingPurchaseId}/edit`,
        search: withHiringGoalId(searchParams),
      });
    };
  };

  const getTooltipContent = (rowItem: ActiveSourcingChannelDetails) => {
    const baseContent = intl.formatMessage({
      defaultMessage:
        'The duration of the campaign within the hiring goal timeframe.',
      description:
        'Duration column tooltip: explains overlap between campaign and hiring period',
    });

    if (
      rowItem.status !== 'completed' &&
      !(
        rowItem.reach_campaign_id &&
        rowItem.end_at &&
        isPast(rowItem.end_at)
      ) &&
      (rowItem.overlap_start_date || rowItem.starts_at) &&
      (rowItem.overlap_end_date || rowItem.end_at)
    ) {
      return (
        <>
          {baseContent}
          <br />
          <br />
          {formatShortDate(
            rowItem.overlap_start_date ?? rowItem.starts_at,
          )} - {formatShortDate(rowItem.overlap_end_date ?? rowItem.end_at)}
        </>
      );
    }

    return baseContent;
  };

  return [
    {
      id: 'sourcing_channel',
      accessorKey: 'sourcing_channel',
      cell: ({ row }) => {
        const { name: channelName, slug: channelSlug } =
          row.original.sourcing_channel;

        return (
          <>
            <div className="flex items-center gap-2">
              {row.original.reach_campaign_id && (
                <ReturnBackArrowIcon className="text-fx-neutral-400" />
              )}
              {/* No logo for Others */}
              {channelSlug !== OTHER ? (
                <ChannelIcon channelSlug={channelSlug} />
              ) : null}
              <Typography variant="h6">{channelName}</Typography>
            </div>
          </>
        );
      },
    },
    {
      accessorKey: 'budget_stats',
      cell: ({ row }) => {
        const channelSlug = row.original.sourcing_channel.slug;

        return (
          <>
            {!row.original.reach_campaign_id && (
              <div className="flex items-center gap-2">
                <Typography variant="caption">
                  {intl.formatMessage({
                    defaultMessage: 'Spend',
                    description:
                      'Channels table: column caption for money spent on this channel',
                  })}
                </Typography>
                {row.original.budget_stats.last_updated_at &&
                  row.original.sourcing_channel.is_integratable && (
                    <Timestamp
                      lastUpdatedAt={row.original.budget_stats.last_updated_at}
                    />
                  )}
              </div>
            )}
            {channelSlug === POOL ? (
              <Typography variant="large2" className="!text-fx-neutral-400">
                <FormattedMessage
                  defaultMessage="Free"
                  description="Free of cost"
                />
              </Typography>
            ) : (
              <CurrencyStats
                stats={row.original.budget_stats}
                onClick={
                  !row.original.is_readonly &&
                  channelSlug !== REACH &&
                  row.original.status !== 'completed'
                    ? handleCampaignEdit(row.original)
                    : undefined
                }
                typographyVariant="large2"
                showProgress
                tooltipContent={intl.formatMessage({
                  defaultMessage:
                    'The spend amount allocated to this channel within the hiring goal timeframe',
                  description:
                    'Stat tooltip: budget consumed on this channel in the current goal window',
                })}
                isApproximate={!row.original.sourcing_channel.is_integratable}
                showLockIcon={
                  channelSlug === ZIPRECRUITER || channelSlug === BOOST
                }
              />
            )}
          </>
        );
      },
    },
    {
      accessorKey: 'duration',
      cell: ({ row }) => {
        const isReachCompleted = Boolean(
          row.original.reach_campaign_id &&
          row.original.end_at &&
          isPast(row.original.end_at),
        );
        return (
          <>
            {!row.original.reach_campaign_id && (
              <Typography variant="caption">
                {intl.formatMessage({
                  defaultMessage: 'Duration (Days)',
                  description:
                    'Channels table: label for how many days the campaign runs',
                })}
              </Typography>
            )}
            <Duration
              stats={row.original.duration_stats}
              brand={brand}
              location={location}
              funnel={funnel}
              typographyVariant="large2"
              showDays={false}
              showProgress
              showDateRange={
                row.original.status === 'completed' || isReachCompleted
              }
              startDate={
                row.original.overlap_start_date ?? row.original.starts_at
              }
              endDate={row.original.overlap_end_date ?? row.original.end_at}
              tooltipContent={getTooltipContent(row.original)}
              onAddButtonClick={handleCampaignEdit(row.original)}
              channelSlug={row.original.sourcing_channel.slug}
            />
          </>
        );
      },
    },
    {
      accessorKey: 'applicants_stats',
      cell: ({ row }) => (
        <>
          {!row.original.reach_campaign_id && (
            <Typography variant="caption">
              {intl.formatMessage({
                defaultMessage: 'Applicants',
                description:
                  'Channels table: column caption for number of applicants',
              })}
            </Typography>
          )}
          <ApplicantStats
            stats={row.original.applicants_stats}
            typographyVariant="large2"
          />
        </>
      ),
    },
    {
      accessorKey: 'cpa_stats',
      cell: ({ row }) => (
        <>
          {!row.original.reach_campaign_id && (
            <Typography variant="caption">
              {intl.formatMessage({
                defaultMessage: 'CPA',
                description:
                  'Channels table: cost-per-applicant acronym (not spelled out for layout)',
              })}
            </Typography>
          )}
          {row.original.sourcing_channel.slug === POOL ? (
            <Typography variant="large2" className="!text-fx-neutral-400">
              <FormattedMessage
                defaultMessage="Free"
                description="Displayed when a Pool channel has no cost for CPA (no spend)"
              />
            </Typography>
          ) : (
            <CurrencyStats
              stats={row.original.cpa_stats}
              onClick={
                row.original.permissions.is_publishable &&
                row.original.status !== 'completed'
                  ? handleCampaignEdit(row.original)
                  : undefined
              }
              typographyVariant="large2"
              separator={<Separator />}
              tooltipContent={intl.formatMessage({
                defaultMessage:
                  'The estimated cost per applicant for this channel based on the budget and target number of applicants',
                description:
                  'Stat tooltip: derived CPA from budget and applicant target',
              })}
            />
          )}
        </>
      ),
    },
    {
      accessorKey: 'conversion_stats',
      cell: ({ row }) => (
        <>
          {!row.original.reach_campaign_id && (
            <Typography variant="caption">
              {intl.formatMessage({
                defaultMessage: 'Conversion',
                description:
                  'Channels table: column caption for applicant-to-hire conversion',
              })}
            </Typography>
          )}
          <ConversionStats
            stats={row.original.conversion_stats}
            onClick={
              row.original.status !== 'completed'
                ? handleCampaignEdit(row.original)
                : undefined
            }
            typographyVariant="large2"
            separator={<Separator />}
            tooltipContent={intl.formatMessage({
              defaultMessage:
                'The predicted conversion rate of applicants to hires based on target numbers',
              description:
                'Stat tooltip: forecast conversion from targets, not only actuals',
            })}
          />
        </>
      ),
    },
    {
      accessorKey: 'hire_stats',
      cell: ({ row }) => (
        <>
          {!row.original.reach_campaign_id && (
            <Typography variant="caption">
              {intl.formatMessage({
                defaultMessage: 'Hires',
                description:
                  'Channels table: column caption for number of hires attributed',
              })}
            </Typography>
          )}
          <ApplicantStats
            stats={row.original.hire_stats}
            typographyVariant="large2"
          />
        </>
      ),
    },
    {
      id: 'edit',
      cell: ({ row }) => {
        const {
          status,
          sourcing_purchase_id: sourcingPurchaseId,
          sourcing_channel: channel,
          reach_campaign_id: reachCampaignId,
          is_readonly: isReadonly,
          permissions,
        } = row.original;

        return (
          <div className="flex flex-col items-center justify-between gap-2">
            {/* Show the campaign toggle switch for integratable channels and non intergratable(but publishable) channels */}
            {sourcingPurchaseId &&
              permissions.is_publishable &&
              (status === 'active' || status === 'stopped') && (
                <Tooltip
                  content={
                    isReadonly &&
                    intl.formatMessage({
                      defaultMessage:
                        'Pausing or resuming campaigns isn’t available yet for this channel.',
                      description:
                        'Tooltip shown when users attempt to pause or resume a campaign in channels where this functionality is not yet supported but will be available in the future. Indicates a temporary limitation.',
                    })
                  }
                >
                  <span>
                    <ToggleCampaign
                      sourcingPurchaseId={sourcingPurchaseId}
                      reachCampaignId={reachCampaignId ?? null}
                      status={status}
                      channel={channel.slug}
                      setReachPublisherStatus={setReachPublisherStatus}
                      isDisabled={isReadonly}
                    />
                  </span>
                </Tooltip>
              )}

            {/* Show the completed tag for completed campaigns */}
            {status === 'completed' && (
              <Tag
                icon={CheckIcon}
                text={intl.formatMessage({
                  defaultMessage: 'Complete',
                  description:
                    'Status tag: campaign is finished and no longer running',
                })}
                className="bg-fx-green-50 -ml-1"
              />
            )}

            <div className="flex items-center justify-between gap-2">
              {/* Show the cloud icon for non-integratable and non publishable channels */}
              {!channel.is_integratable && !permissions.is_publishable && (
                <img src={RemoveCloud} alt="Remove cloud" />
              )}

              {sourcingPurchaseId && !reachCampaignId && (
                <>
                  {status === 'completed' ? (
                    // Show the details button to view campaign parameters for completed campaigns and non-editable campaigns
                    <Tooltip
                      content={
                        isReadonly &&
                        intl.formatMessage({
                          defaultMessage:
                            'Editing campaign parameters is not available yet for this channel.',
                          description:
                            'Tooltip shown when users try to edit campaign parameters for channels where editing is not currently supported but will be available in the future. Indicates a temporary limitation.',
                        })
                      }
                    >
                      <Button
                        variant="ghost"
                        onClick={handleCampaignEdit(row.original)}
                        className="text-fx-primary hover:bg-fx-neutral-50 h-4 p-0"
                      >
                        <FormattedMessage
                          defaultMessage="Details"
                          description="Read-only campaign: view parameters instead of edit"
                        />
                      </Button>
                    </Tooltip>
                  ) : (
                    // Show the edit button for updating campaign parameters for ongoing campaigns
                    <Button
                      variant="icon"
                      size="icon"
                      onClick={handleCampaignEdit(row.original)}
                    >
                      <img src={EditIcon} alt="Edit" />
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        );
      },
    },
  ];
}

function Separator() {
  return <span className="bg-fx-neutral-300 mx-1 h-6 w-px" />;
}
